/* eslint-disable max-lines */
import React, { useState, useRef, useEffect } from 'react'
import { Moment } from 'moment'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { Tabs } from '@mui/material'
import styled, { css } from 'styled-components'

import TabPanel from 'components/Tabs/TabPanel'

import AlertsPanel from 'pages/Device/AlertsPanel'
import Status from 'pages/Device/Status'
import DashboardStatus from 'pages/Device/SystemDashboard/Status'
import Notes from 'pages/Device/SystemDashboard/Notes'
import Reports from 'pages/Device/SystemDashboard/Reports'
import ConfigComponents from 'pages/Device/SystemDashboard/ConfigComponents'
import Alerts from 'pages/Device/SystemDashboard/Alerts'
import ThermostatRthPanel from 'pages/Device/ThermostatRthPanel'
import ConsumerRthPanel from 'pages/Device/ConsumerRthPanel'
import AwairRthPanelNew from 'pages/Device/AwairRthPanelNew'
import AwairRthPanel, { AwairDevice } from 'pages/Device/AwairRthPanel'
import SystemConfigPanel from 'pages/Device/SystemConfigPanel'
import SystemComponentsPanel from 'pages/Device/SystemComponentsPanel'
import SystemReportsPanel from 'pages/Device/SystemReportsPanel'
import VerificationReportsPanel from 'pages/Device/VerificationReportsPanel'
import NotesPanel from 'pages/Device/NotesPanel'
import MaintenanceTrendsPanel from 'pages/Device/MaintenanceTrendsPanel'
import SystemDashboard from 'pages/Device/SystemDashboard'
import fallbackContent from 'pages/Device/fallbackContent'
import AssignedSystemTime from 'pages/Device/AssignedSystemTime'

import useDevice from 'hooks/useDevice'
import useSearchParams from 'hooks/useSearchParams'
import useTrackScrollPositionOnPage from 'hooks/useTrackScrollPositionOnPage'
import useTrackTimeOnPage from 'hooks/useTrackTimeOnPage'
import useHomeownerAlerts from 'hooks/useHomeownerAlerts'
import useVerificationReports from 'hooks/useVerificationReports'
import useSystemReports from 'hooks/useSystemReports'
import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

import GA4 from 'helpers/ga4'
import Brand from 'helpers/brand'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import KeyboardAccessible from 'components/KeyboardAccessible'
import { useFeaturesContext } from 'hooks/FeaturesContext'

type Props = {
  dealerId: number
  deviceId: string
  isActive: boolean
  isUnassignedDevice?: boolean
  refreshPageCallback?: () => void | null
  className?: string
  customerId?: string
}

type handleTabNavigationProps = {
  selectedTab: string
  ga4Category: string
  ga4Action: string
  ga4Label: string
}

const TABS = {
  DASHBOARD: 'dashboard',
  STATUS: 'status',
  THERMOSTAT_RTH: 'thermostat-rth',
  MAINTENANCE_TRENDS: 'maintenance-trends',
  CUSTOMER_RUNTIME: 'customer-runtime',
  CONFIG_COMPONENTS: 'config-components',
  ALERTS: 'alerts',
  NOTES: 'notes',
  REPORTS: 'reports',
  AWAIR_RTH: 'awair-rth'
}

const Device = ({ dealerId, deviceId, isActive, isUnassignedDevice = false, refreshPageCallback, customerId }: Props) => {
  const { enabledFeatures } = useFeaturesContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { reportCache } = useStoreContext()
  const { session } = useSessionContext()
  const awairRthDate = useSearchParams('awairRthDate')
  const params = useParams()
  const { activeTab } = params
  const newAwairRthEnabled = enabledFeatures.includes('new_awair_rth')

  const { hasAccess, showFeature, hoverTextMessage } = usePremiumFeaturesContext()

  useHomeownerAlerts(deviceId)
  useVerificationReports(deviceId)
  useSystemReports(deviceId)

  const { device, isDeviceLoaded } = useDevice(dealerId, deviceId)
  const [thermostatRthDate, setThermostatRthDate] = useState(useSearchParams('thermostatRthDate'))
  const [selectedTab, setSelectedTab] = useState(activeTab || 'dashboard')

  const rthRef = useRef<HTMLDivElement>(null)
  const brandName = Brand.brandName()

  useEffect(() => {
    const currentPath = location.pathname?.replace(/\/+$/, '')
    const expectedCustomerDevicePath = `customer/${customerId}/systems/${deviceId}`
    const expectedAdminDevicePath = `/devices/${deviceId}`

    const isOnThisDevicePage = currentPath === expectedCustomerDevicePath || currentPath === expectedAdminDevicePath
    const isOnThisDeviceSubPage = currentPath.startsWith(expectedCustomerDevicePath + '/') || currentPath.startsWith(expectedAdminDevicePath + '/')
    if (isOnThisDevicePage || isOnThisDeviceSubPage) {
      if (!activeTab && isOnThisDeviceSubPage) {
        const newPath = `${currentPath}/dashboard`
        navigate(newPath, { replace: true })
      }
    }
  }, [activeTab, location.pathname, deviceId, navigate, customerId])

  const handleTabNavigation = ({ selectedTab, ga4Action, ga4Category, ga4Label }: handleTabNavigationProps) => {
    const currentPath = location.pathname?.replace(/\/+$/, '')
    let newPath = currentPath
    if (selectedTab && selectedTab !== activeTab) {
      newPath = !activeTab ? currentPath : currentPath.replace(activeTab, selectedTab)
      if (currentPath.includes('/systems/') || currentPath.includes('/devices/multiple')) {
        newPath = !activeTab ? currentPath : currentPath.replace(activeTab, selectedTab)
      } else if (currentPath.includes('/devices/')) {
        newPath = `${currentPath}/${selectedTab}`
      }
      setSelectedTab(selectedTab)
    }

    GA4.sendEvent({ category: ga4Category, action: ga4Action, label: ga4Label })

    const searchParams = location.search
    if (searchParams) {
      newPath += searchParams
    }
    navigate(newPath, { replace: true })
  }

  // TODO: This needs to be part of the refactor. We should not be adjusting state during render. Gus is pointing
  // to this stack overflow post: https://stackoverflow.com/questions/35290245/calling-setstate-in-render-is-not-avoidable
  const [noZones, setNoZones] = React.useState(false)
  const handleNoZonesStatus = () => {
    setNoZones(true)
  }

  const earlyReturnJsx = fallbackContent(isDeviceLoaded, device, refreshPageCallback, brandName)

  useTrackTimeOnPage('device_details', [60, 90, 120, 300], !earlyReturnJsx)
  useTrackScrollPositionOnPage('device_details', [50, 75], !earlyReturnJsx)

  if (earlyReturnJsx) {
    return earlyReturnJsx
  }

  if (device) {
    const hasZoneStatus = device.zoningEnabled
    const showMaintenanceTrends = ((showFeature('maintenance_trends') || session.isDeveloper)) &&
      device.hasMaintenanceTrends
    const showVerificationReportsPanel = device.deviceType === 'control_hub'
    const showSystemReports = showFeature('system_reports_history')
    const showDayRthCallback = (time: Moment) => {
      rthRef.current?.scrollIntoView() // navigates from alerts panel
      setThermostatRthDate(time.format('MM-DD-YYYY'))
      setSelectedTab('thermostat-rth')

      navigate({
        pathname: activeTab ? location.pathname.replace(activeTab, 'thermostat-rth') : location.pathname + '/thermostat-rth',
        search: `?thermostatRthDate=${time.format('MM-DD-YYYY')}`
      })
    }

    const showDashboard = selectedTab === TABS.DASHBOARD

    const tabs: Array<{
      enabled: boolean,
      label: string,
      link: string,
      icon?: string,
      toolTipText?: string,
      details: React.ReactNode | null,
      premiumTabName?: string,
      premiumDefaultHover?: string
    }> = [{
      label: 'Dashboard',
      link: TABS.DASHBOARD,
      enabled: true,
      toolTipText: 'Click to see Dashboard overview.',
      details: null
    }]

    tabs.push({
      label: 'Thermostat Runtime',
      link: TABS.THERMOSTAT_RTH,
      details: (
        <ThermostatRthPanel
          isActive={isActive}
          device={device}
          timeZone={device.timeZone}
          thermostatRthDate={thermostatRthDate}
          ref={rthRef}
          reportCache={reportCache}
          updateThermostatRthDateCallback={setThermostatRthDate}
        />
      ),
      icon: 'rtgraph.png',
      toolTipText: 'Click to see Thermostat Runtime History Graph.',
      enabled: true
    })

    if (showMaintenanceTrends) {
      tabs.push({
        premiumTabName: 'maintenance_trends',
        premiumDefaultHover: 'Click to see Maintenance Trends.',
        label: 'Maintenance Trends',
        link: TABS.MAINTENANCE_TRENDS,
        details: <MaintenanceTrendsPanel device={device} />,
        icon: 'trends.png',
        enabled: hasAccess('maintenance_trends') || session.isDeveloper,
        toolTipText: hoverTextMessage(
          'maintenance_trends',
          'Click to see Maintenance Trends.'
        )
      })
    }

    if (device.consumerRth) {
      tabs.push({
        label: 'Customer Runtime',
        link: TABS.CUSTOMER_RUNTIME,
        details: (
          <ConsumerRthPanel device={device} />
        ),
        icon: 'crtgraph.png',
        toolTipText: 'Click to see Customer Runtime History Graph.',
        enabled: true
      })
    }

    if (hasZoneStatus || !noZones) {
      tabs.push({
        label: showDashboard ? 'Status' : 'Status/Zones',
        link: TABS.STATUS,
        enabled: true,
        toolTipText: 'Click to see more Status/Zones details.',
        details: (
          showDashboard ? (
            <DashboardStatus
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
              onNoZones={handleNoZonesStatus}
              deviceModel={device.deviceModel}
            />
          ) : (
            <Status
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              deviceModel={device.deviceModel}
              reportCache={reportCache}
            />
          )
        )
      })
    } else {
      tabs.push({
        label: showDashboard ? 'Status' : 'System Status',
        link: TABS.STATUS,
        enabled: true,
        toolTipText: 'Click to see more System Status details.',
        details: (
          showDashboard ? (
            <DashboardStatus
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
              onNoZones={handleNoZonesStatus}
              deviceModel={device.deviceModel}
            />
          ) : (
            <Status
              deviceId={deviceId}
              deviceModel={device.deviceModel}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
            />
          )
        )
      })
    }

    tabs.push({
      label: 'config/components',
      link: TABS.CONFIG_COMPONENTS,
      enabled: true,
      toolTipText: 'Click to see more System Configuration and Component details.',
      details: (showDashboard
        ? <ConfigComponents deviceId={deviceId} reportCache={reportCache} />
        : (
          <>
            <SystemConfigPanel
              deviceId={deviceId}
              deviceType={device.deviceType}
              reportCache={reportCache}
              readOnly={session.isFsr || session.isTechnician || isUnassignedDevice}
            />
            <SystemComponentsPanel
              deviceId={deviceId}
              communicating={device.communicating}
              reportCache={reportCache}
              readOnly={session.isFsr || session.isTechnician || isUnassignedDevice}
            />
          </>
        )
      )
    })

    if (device.hasAwairDevice) {
      tabs.push({
        label: 'Awair Runtime History',
        link: TABS.AWAIR_RTH,
        details: (
          <>
            {newAwairRthEnabled && <AwairRthPanelNew
              deviceId={deviceId}
              timeZone={device.timeZone}
              datesWithData={device.awairDataPresence ?? []}
              awairDevices={device.awairDevices as unknown as AwairDevice[]}
              awairRthDate={awairRthDate}
                                   />}
            <AwairRthPanel
              deviceId={deviceId}
              timeZone={device.timeZone}
              datesWithData={device.awairDataPresence ?? []}
              awairDevices={device.awairDevices as unknown as AwairDevice[]}
              awairRthDate={awairRthDate}
            />
          </>
        ),
        icon: 'awair.png',
        toolTipText: 'Click to see Awair Runtime History Graph.',
        enabled: true
      })
    }

    tabs.push({
      label: 'Alerts',
      icon: 'alerts.png',
      link: TABS.ALERTS,
      enabled: true,
      toolTipText: 'Click to see Active Alerts and Alert History.',
      details: showDashboard
        ? <Alerts
          deviceId={deviceId}
          reportCache={reportCache}
          />
        : <AlertsPanel
          deviceId={deviceId}
          reportCache={reportCache}
          deviceTimeZone={device.timeZone}
          showDayRthCallback={showDayRthCallback}
          />
    })

    if (showVerificationReportsPanel || showSystemReports) {
      tabs.push({
        label: 'Reports',
        link: TABS.REPORTS,
        enabled: true,
        toolTipText: hoverTextMessage('system_reports_history', 'Click to view System Reports.'),
        details: (
          showDashboard
            ? <Reports showSystemReports={showSystemReports}
              showVerificationReports={showVerificationReportsPanel}
              />
            : (
              <>
                {showSystemReports && <SystemReportsPanel />}
                {showVerificationReportsPanel && <VerificationReportsPanel />}
              </>
            )
        )
      })
    }

    if (!session.isFsr) {
      tabs.push({
        label: 'Notes',
        link: TABS.NOTES,
        enabled: true,
        toolTipText: 'Click to view or edit Notes for the system.',
        details: (
          showDashboard
            ? <Notes notes={device.note} />
            : <NotesPanel device={device} refreshPageCallback={refreshPageCallback} />
        )
      })
    }

    const getTabIndex = (tab: string) => {
      const filteredTabs = tabs.filter(item => item.enabled)
      const index = filteredTabs.findIndex(item => item.link === tab)

      return index !== -1 ? index : 0
    }

    const renderDeviceDashboardIcon = () => {
      return (
        <S.DashboardIcon
          src="/img/customize.svg"
          aria-hidden="true"
          data-testid="device-dashboard-icon"
        />
      )
    }

    return (
      <S.Container isDashboard={showDashboard}>
        <S.Tabs
          variant="scrollable"
          value={getTabIndex(selectedTab)}
          aria-label="device tabs"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ sx: { display: 'none' } }}
        >
          {tabs.map(
            (tab) =>
              tab.enabled && (
                <S.TabButton
                  key={tab.link}
                  onClick={() =>
                    handleTabNavigation({
                      selectedTab: tab.link,
                      ga4Category: 'Customer Detail Page',
                      ga4Action: 'User click device details nav button',
                      ga4Label: tab.label
                    })
                  }
                  isActive={selectedTab === tab.link}
                >
                  {tab.link === TABS.DASHBOARD && renderDeviceDashboardIcon()}
                  <S.TabLabel isActive={selectedTab === tab.link}>
                    {tab.label}
                  </S.TabLabel>
                </S.TabButton>
              )
          )}
        </S.Tabs>

        {showDashboard ? (
          <SystemDashboard
            tabs={tabs}
            navigationCallback={handleTabNavigation}
            isZoned={!!device.zoningEnabled}
          />
        ) : (
          tabs.map(
            (item) =>
              item.enabled && (
                <TabPanel
                  key={item.link}
                  hidden={selectedTab !== item.link}
                  id={item.link}
                  ariaLabelledby={item.link}
                >
                  {item.details}
                </TabPanel>
              )
          )
        )}

        <AssignedSystemTime
          device={device}
          refreshPageCallback={refreshPageCallback}
        />
      </S.Container>
    )
  }
}

export default Device

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isDashboard'].includes(prop)
  }) <{ isDashboard: boolean }>`
        position: relative;
        display: block;
        padding: 0;
        background-color: ${color('paneBackground')};
        margin: 0px 10px 10px;
        @media ${screenSize.medium} {
            padding: 0;
            margin: 0;
        }
    `,
  Tabs: styled(Tabs)`
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        flex-direction: row;
        position: sticky;
        z-index: 99;
        top: 0;
        background-color: ${color('paneBackground')};
        align-items: center;
        padding: 0.5rem 1rem;
        @media ${screenSize.medium} {
            flex-wrap: nowrap;
            padding: 0.5rem 0rem;
        }

        & .MuiTabs-flexContainer {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            flex-direction: row;
            justify-content: flex-start;
            gap: 1.5rem;
            @media ${screenSize.medium} {
                flex-wrap: nowrap;
            }
        }

        & .MuiTabs-scrollButtons svg {
            font-size: 2.5rem;
        }
    `,
  TabButton: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop)
  }) <{ isActive: boolean }>`
        position: relative;
        background: none;
        border: none;
        cursor: pointer;
        color: ${({ isActive }) => (isActive ? color('activeTabHighlight') : color('modalOverlay'))};
        z-index: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 2px;
        transition: border-bottom 0.4s ease-in-out;
        border-bottom: ${({ isActive }) => `4px solid ${color(isActive ? 'activeTabHighlight' : 'paneBackground')}`};
    `,
  TabLabel: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop)
  }) <{ isActive: boolean }>`
        position: relative;
        display: inline-block;
        cursor: pointer;
        font-family: 'Inter', sans-serif;
        font-size: 1rem;
        font-weight: ${({ isActive }) => (isActive ? '500' : '600')};
        text-transform: capitalize;
        min-height: 30px;
        line-height: 30px;

        ${props => props.isActive &&
      css`
                    &::after {
                        width: 100%;
                    }
                `}
    `,
  DashboardIcon: styled.img`
        width: 24px;
        height: 24px;
    `
}
